import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Plus, X, Loader, Save, RefreshCw, FileText, Trash2, Undo2, Pencil, Paperclip, Lock, KeyRound, ShieldCheck, Upload, CheckCircle2,
} from 'lucide-react';
import { api as plansApi, fmtDate, fmtMoney, labelStyle, inputStyle, th, td, num, GREEN, AMBER, RED } from '../utils/atoShared';
import { returnPayment } from '../utils/atoReturnPay';
import { todayIso } from '../utils/atoPlan';
import { vaultCall as call, checkVaultFile as checkReturnFile, expandZips, prepareVaultFile, useVault, openVaultFile, VaultLock, ChangePassword } from './AtoVault';

// ── Tax returns ─────────────────────────────────────────────────────────────────
// The lodged returns for Paul, Holly and the businesses, behind a SECOND lock: the page
// is already Paul-only, and a return also needs his own vault password (a return carries
// the TFN). The lock, token and file opener are shared with Tax documents (AtoVault.jsx);
// ato-returns-sb enforces it and keeps the list and every file encrypted.
//
// Drop a stack of returns — or the accountant's .zip — on the tab: each PDF is read and
// filed as its own return, or attached to the one already there for that entity + year.
// Nothing is deleted: Remove hides a return (Restore brings it back), files stay.

const ENTITY_TYPES = ['Individual', 'Company', 'Trust', 'Partnership', 'SMSF'];
const DOC_KINDS = ['Tax return', 'Return, financials & minutes', 'Notice of assessment', 'Return summary', 'Workpapers', 'Other'];
const ISO = /^\d{4}-\d{2}-\d{2}$/;
// Claude reads the first pages only: page 1 of an accountant's return pack is the summary
// with the figures, and a 50-page pack would otherwise run past the read's time limit.
const READ_PAGES = 12;

// The accountant names each file "<Entity> - 2025 Income Tax Return, Financials & Minutes
// [ref].pdf". That's the cleanest entity name there is, and "2025" is the year ended
// 30 June 2025 = income year 2024–25.
function fromFileName(name) {
  const base = name.replace(/\.pdf.*$/i, '');
  const m = base.match(/^(.+?)\s+-\s+(\d{4})\b/);
  const out = {};
  if (m) { out.entity = m[1].trim(); const y = Number(m[2]); out.year = `${y - 1}–${String(y).slice(2)}`; }
  out.kind = /financ/i.test(base) ? 'Return, financials & minutes' : /assessment/i.test(base) ? 'Notice of assessment' : 'Tax return';
  return out;
}
const sameKey = (entity, year) => `${String(entity).toUpperCase().replace(/^THE TRUSTEE FOR\s+/, '').replace(/[^A-Z0-9]/g, '')}|${String(year).replace(/[^0-9]/g, '')}`;

// The last completed income year, e.g. '2025–26' from 1 July 2026 — the one being lodged.
function lastYear() {
  const d = new Date();
  const end = d.getMonth() >= 6 ? d.getFullYear() : d.getFullYear() - 1; // last completed year ends 30 Jun `end`
  return `${end - 1}–${String(end).slice(2)}`;
}
const blank = () => ({ entity: '', entityType: 'Individual', abn: '', year: lastYear(), lodged: '', totalIncome: '', taxableIncome: '', taxPayable: '', result: '', agent: '', notes: '' });

const drawerWrap = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' };
const drawerBody = { width: 560, maxWidth: '100%', background: 'var(--hp-bg)', borderLeft: '1px solid var(--hp-border)', height: '100%', overflowY: 'auto', padding: 24 };
const dropZone = { border: '2px dashed var(--hp-border)', borderRadius: 10, padding: 14, textAlign: 'center' };
const kicker = { fontFamily: 'var(--font-mono)', fontSize: '.65rem', textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--hp-gold)', marginBottom: 4 };

// ── the stamp ───────────────────────────────────────────────────────────────────
// A rubber stamp on each return whose tax is settled: PAID + the day it was fully paid,
// or PAYMENT PLAN (with what's paid and left) while it sits on a plan. Worked out live from
// the Payment plans and Tax paid tabs (utils/atoReturnPay), never stored on the return, so
// recording a payment there is all it takes. Notices for the same income year ride under it.
const stampBox = (color) => ({
  display: 'inline-block', transform: 'rotate(-4deg)', border: `2px solid ${color}`, outline: `1px solid ${color}`, outlineOffset: 2,
  color, borderRadius: 4, padding: '2px 8px', fontFamily: 'var(--font-mono)', fontWeight: 800, fontSize: '.72rem',
  letterSpacing: '.12em', textTransform: 'uppercase', whiteSpace: 'nowrap', lineHeight: 1.3, textAlign: 'center',
});
const PLAN_BLUE = 'var(--hp-blue, #3b82f6)';
function PayStamp({ pay }) {
  if (!pay) return null;
  const rel = (pay.related || []).map(({ item, work }) => {
    const done = work.status === 'paid' || work.status === 'late';
    return (
      <div key={item.id} style={{ fontSize: '.72rem', color: done ? GREEN : work.status === 'overdue' ? RED : 'var(--hp-dim)', marginTop: 4, whiteSpace: 'nowrap' }}
        title={[item.docType, item.kind, item.period].filter(Boolean).join(' · ')}>
        + {fmtMoney(item.amount)} {(item.docType || 'notice').toLowerCase()}: {done ? `paid ${fmtDate(work.coveredOn)}` : work.status === 'overdue' ? 'overdue' : item.dueDate ? `pay by ${fmtDate(work.payBy || item.dueDate)}` : 'to pay'}
      </div>
    );
  });
  let main = null;
  if (pay.stamp === 'paid') {
    main = <span style={stampBox(GREEN)} title={pay.via === 'plan' ? `Payment plan ${pay.plan.label || ''} paid off` : 'Paid — on the Tax paid tab'}>Paid<br /><span style={{ fontSize: '.66rem', letterSpacing: '.06em' }}>{pay.date ? fmtDate(pay.date) : ''}</span></span>;
  } else if (pay.stamp === 'plan') {
    main = (
      <div>
        <span style={stampBox(PLAN_BLUE)} title={`On the payment plan ${pay.plan.label || ''}`}>Payment plan</span>
        <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginTop: 5, whiteSpace: 'nowrap' }}>{fmtMoney(pay.paid)} paid · {fmtMoney(pay.left)} left</div>
      </div>
    );
  } else if (pay.stamp === 'part') {
    main = (
      <div>
        <span style={stampBox(AMBER)}>Part-paid</span>
        <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginTop: 5, whiteSpace: 'nowrap' }}>{fmtMoney(pay.paid)} paid · {fmtMoney(pay.left)} owing</div>
      </div>
    );
  } else if (pay.note === 'unpaid') {
    main = <span style={{ fontSize: '.74rem', color: pay.overdue ? RED : AMBER, whiteSpace: 'nowrap' }}>{pay.overdue ? 'Overdue' : `To pay${pay.payBy ? ` by ${fmtDate(pay.payBy)}` : ''}`}</span>;
  } else if (pay.note === 'none') {
    main = <span style={{ fontSize: '.74rem', color: 'var(--hp-dim)', whiteSpace: 'nowrap' }} title="No payment plan or Tax paid entry for this amount">No payment recorded</span>;
  }
  if (!main && !rel.length) return <span style={{ color: 'var(--hp-dim)' }}>—</span>;
  return <div>{main}{rel}</div>;
}

// ── add / edit a return ─────────────────────────────────────────────────────────
function ReturnDrawer({ ret, knownEntities, unlock, onLocked, onClose, onSaved, notify }) {
  const [f, setF] = useState(() => (ret ? { ...blank(), ...ret } : blank()));
  const [file, setFile] = useState(null);       // { prep, name } — prep from prepareVaultFile
  const [fileKind, setFileKind] = useState('Tax return');
  const [reading, setReading] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const take = async (fl) => {
    if (!checkReturnFile(fl, notify)) return;
    setReading(true);
    try {
      const prep = await prepareVaultFile(fl, { unlock, maxReadPages: READ_PAGES });
      setFile({ prep, name: fl.name });
      const byName = fromFileName(fl.name);
      if (byName.entity) setF((cur) => ({ ...cur, entity: cur.entity || byName.entity, year: byName.year || cur.year }));
      setFileKind(byName.kind);
      if (!prep.readBase64) { notify?.('Too big for Claude to read — fill in the figures by hand', 'info'); return; }
      const r = await call('read', { unlock, base64: prep.readBase64, contentType: prep.contentType });
      if (r.error === 'VAULT_LOCKED') { onLocked(); return; }
      if (!r.success) { notify?.(r.error || 'Could not read it — fill it in by hand', 'info'); return; }
      const x = r.extracted || {};
      setF((cur) => ({
        ...cur,
        entity: cur.entity || x.entity || '',
        entityType: ENTITY_TYPES.includes(x.entityType) ? x.entityType : cur.entityType,
        abn: cur.abn || String(x.abn || '').replace(/\D/g, ''),
        year: x.year || cur.year,
        lodged: /^\d{4}-\d{2}-\d{2}$/.test(x.lodged || '') ? x.lodged : cur.lodged,
        totalIncome: Number(x.totalIncome) ? Number(x.totalIncome) : cur.totalIncome,
        taxableIncome: Number(x.taxableIncome) ? Number(x.taxableIncome) : cur.taxableIncome,
        taxPayable: Number(x.taxPayable) ? Number(x.taxPayable) : cur.taxPayable,
        result: Number(x.result) ? Number(x.result) : cur.result,
        agent: cur.agent || x.agent || '',
      }));
      if (DOC_KINDS.includes(x.docType) && !byName.entity) setFileKind(x.docType);
      notify?.('Read it — check the figures before saving', 'success');
    } catch (e) { notify?.(e.message, 'error'); }
    finally { setReading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = file ? { ...(await file.prep.upload()), fileKind } : {};
      const r = await call('save', { unlock, ret: f, ...payload });
      if (r.error === 'VAULT_LOCKED') { onLocked(); return; }
      if (!r.success) { notify?.(r.error || 'Save failed', 'error'); return; }
      notify?.('Return saved', 'success');
      onSaved(r.ret);
    } catch (e) { if (e.message === 'VAULT_LOCKED') onLocked(); else notify?.(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const field = (k, label, type = 'text', extra = {}) => (
    <div><label style={labelStyle}>{label}</label>
      <input type={type} step={type === 'number' ? '0.01' : undefined} className="input" style={inputStyle} value={f[k] ?? ''} onChange={(e) => set(k, e.target.value)} {...extra} /></div>
  );

  return (
    <div style={drawerWrap} onClick={onClose}>
      <div style={drawerBody} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div><div style={kicker}>Tax return</div><div style={{ fontWeight: 800, fontSize: '1.1rem' }}>{ret ? `${ret.entity} · ${ret.year}` : 'Add a return'}</div></div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        <div style={{ ...dropZone, marginBottom: 18 }} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); take(e.dataTransfer?.files?.[0]); }}>
          {reading ? <div style={{ color: 'var(--hp-dim)' }}><Loader size={14} className="spin" /> Reading the return…</div> : file ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              <FileText size={15} /> <span style={{ fontSize: '.86rem' }}>{file.name}</span>
              <select className="input" style={{ ...inputStyle, width: 'auto' }} value={fileKind} onChange={(e) => setFileKind(e.target.value)}>{DOC_KINDS.map((k) => <option key={k}>{k}</option>)}</select>
              <button className="btn btn-ghost btn-sm" onClick={() => setFile(null)}><X size={13} /></button>
            </div>
          ) : (
            <label style={{ cursor: 'pointer', display: 'block' }}>
              <div style={{ fontWeight: 600 }}>{ret ? 'Add another file' : 'Drop the return PDF here'}</div>
              <div style={{ color: 'var(--hp-dim)', fontSize: '.8rem' }}>or click to choose · Claude reads who, which year and the figures</div>
              <input type="file" accept="application/pdf,image/*" style={{ display: 'none' }} onChange={(e) => take(e.target.files?.[0])} />
            </label>
          )}
        </div>

        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Entity</label>
            <input className="input" style={inputStyle} list="atr-entities" value={f.entity} onChange={(e) => set('entity', e.target.value)} placeholder="e.g. PAUL BOUVENG" />
            <datalist id="atr-entities">{knownEntities.map((e) => <option key={e} value={e} />)}</datalist>
          </div>
          <div><label style={labelStyle}>Type</label>
            <select className="input" style={inputStyle} value={f.entityType} onChange={(e) => set('entityType', e.target.value)}>{ENTITY_TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
          {field('year', 'Income year', 'text', { placeholder: '2024–25' })}
          {field('abn', 'ABN (if any)')}
          {field('lodged', 'Lodged / assessed', 'date')}
          {field('totalIncome', 'Total income ($)', 'number')}
          {field('taxableIncome', 'Taxable income ($)', 'number')}
          {field('taxPayable', 'Tax on taxable income ($)', 'number')}
          {field('result', 'Payable (+) / refund (−) ($)', 'number')}
          <div style={{ gridColumn: '1 / -1' }}>{field('agent', 'Tax agent')}</div>
          <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Notes</label>
            <textarea className="input" style={{ ...inputStyle, minHeight: 60 }} value={f.notes} onChange={(e) => set('notes', e.target.value)} /></div>
        </div>
        <p style={{ color: 'var(--hp-dim)', fontSize: '.78rem' }}>Don’t type a TFN into any field. It’s on the PDF, and the PDF is stored encrypted.</p>
        <button className="btn btn-primary" onClick={save} disabled={saving || reading}>{saving ? <Loader size={14} className="spin" /> : <Save size={14} />} Save</button>
      </div>
    </div>
  );
}

// ── the tab ─────────────────────────────────────────────────────────────────────
export default function TaxReturnsTab({ notify, knownEntities = [] }) {
  const { status, unlock, lock: vaultLock, onUnlocked } = useVault(notify);
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawer, setDrawer] = useState(null);   // { ret } | { ret: null }
  const [showRemoved, setShowRemoved] = useState(false);
  const [changing, setChanging] = useState(false);
  const [opening, setOpening] = useState('');
  const [queue, setQueue] = useState([]);       // [{ name, state, note }] for a bulk drop

  const lock = useCallback(() => { vaultLock(); setReturns([]); setDrawer(null); }, [vaultLock]);
  // Locked by the 15-minute timer too: don't keep decrypted records in memory.
  useEffect(() => { if (!unlock) { setReturns([]); setDrawer(null); } }, [unlock]);

  const load = useCallback(async () => {
    if (!unlock) return;
    setLoading(true);
    try {
      const r = await call('list', { unlock });
      if (r.error === 'VAULT_LOCKED') { lock(); return; }
      if (r.success) setReturns(r.returns); else notify?.(r.error || 'Could not load returns', 'error');
    } catch (e) { notify?.(e.message, 'error'); }
    finally { setLoading(false); }
  }, [unlock, lock, notify]);
  useEffect(() => { load(); }, [load]);

  // The money side, for the PAID / PAYMENT PLAN stamps: plans + Tax paid (same Paul-only
  // gate as the rest of the page; not vault data, so no unlock token needed).
  const [money, setMoney] = useState(null);
  const loadMoney = useCallback(async () => {
    try {
      const [p, t] = await Promise.all([plansApi('load'), plansApi('loadTax')]);
      if (p.success && t.success) setMoney({ plans: p.plans, planPayments: p.payments, items: t.items, taxPayments: t.payments });
    } catch { /* stamps just don't show */ }
  }, []);
  useEffect(() => { if (unlock) loadMoney(); }, [unlock, loadMoney]);

  const upsert = (ret) => setReturns((rs) => (rs.some((x) => x.id === ret.id) ? rs.map((x) => (x.id === ret.id ? ret : x)) : [...rs, ret]));

  const openDoc = async (ret, doc) => {
    setOpening(doc.id);
    try { await openVaultFile('getDoc', { id: ret.id, docId: doc.id }, { unlock, onLocked: lock, notify }); }
    finally { setOpening(''); }
  };

  const addFile = async (ret, fl) => {
    if (!checkReturnFile(fl, notify)) return;
    try {
      const prep = await prepareVaultFile(fl, { unlock });
      const r = await call('addDoc', { unlock, id: ret.id, ...(await prep.upload()), fileKind: fromFileName(fl.name).kind });
      if (r.error === 'VAULT_LOCKED') { lock(); return; }
      if (r.success) { upsert(r.ret); notify?.('File added', 'success'); } else notify?.(r.error || 'Upload failed', 'error');
    } catch (e) { if (e.message === 'VAULT_LOCKED') lock(); else notify?.(e.message, 'error'); }
  };

  // A stack of returns or a .zip: one file at a time — unpack, read, upload, file it.
  // Entity + year come from the accountant's file name when it has them (else from the
  // read); the figures from the read. Same entity + year as a return already here →
  // the file is attached to that one, not filed twice.
  const ingest = async (fileList) => {
    const files = (await expandZips(fileList, notify)).filter((f) => checkReturnFile(f, notify));
    if (!files.length) return;
    setQueue(files.map((f) => ({ name: f.name, state: 'waiting' })));
    const mark = (i, patch) => setQueue((qs) => qs.map((x, j) => (j === i ? { ...x, ...patch } : x)));
    const known = new Map(returns.filter((r) => !r.deletedAt).map((r) => [sameKey(r.entity, r.year), r]));
    let saved = 0;
    for (let i = 0; i < files.length; i++) {
      const fl = files[i];
      try {
        mark(i, { state: 'reading', note: 'reading…' });
        const byName = fromFileName(fl.name);
        const prep = await prepareVaultFile(fl, { unlock, maxReadPages: READ_PAGES, onProgress: (n) => mark(i, { note: n }) });
        let x = {};
        if (prep.readBase64) {
          const r = await call('read', { unlock, base64: prep.readBase64, contentType: prep.contentType });
          if (r.error === 'VAULT_LOCKED') { lock(); return; }
          if (r.success) x = r.extracted || {};
        }
        const entity = byName.entity || String(x.entity || '').trim();
        const year = byName.year || x.year || '';
        if (!entity || !year) { mark(i, { state: 'error', note: 'couldn’t tell whose return or which year — add it with Add return' }); continue; }
        const existing = known.get(sameKey(entity, year));
        // The same file dropped twice (same byte count on that return) is not stored again.
        if (existing && (existing.docs || []).some((d) => d.size === prep.size)) { mark(i, { state: 'done', note: `already on ${entity} · ${year} — skipped` }); continue; }
        mark(i, { state: 'saving', note: 'uploading…' });
        const payload = { ...(await prep.upload()), fileKind: byName.kind };
        const r = existing
          ? await call('addDoc', { unlock, id: existing.id, ...payload })
          : await call('save', { unlock, ...payload, ret: {
            ...blank(), entity, year,
            entityType: ['Individual', 'Company', 'Trust', 'Partnership', 'SMSF'].includes(x.entityType) ? x.entityType : 'Individual',
            abn: String(x.abn || '').replace(/\D/g, ''),
            lodged: ISO.test(x.lodged || '') ? x.lodged : '',
            totalIncome: Number(x.totalIncome) || '', taxableIncome: Number(x.taxableIncome) || '',
            taxPayable: Number(x.taxPayable) || '', result: Number(x.result) || '', agent: x.agent || '',
          } });
        if (r.error === 'VAULT_LOCKED') { lock(); return; }
        if (!r.success) { mark(i, { state: 'error', note: r.error }); continue; }
        upsert(r.ret); known.set(sameKey(entity, year), r.ret); saved++;
        mark(i, { state: 'done', note: existing ? `added to ${entity} · ${year}` : `${entity} · ${year}${prep.readBase64 && x.entity ? '' : ' (figures not read — fill them in)'}` });
      } catch (e) {
        if (e.message === 'VAULT_LOCKED') { lock(); return; }
        mark(i, { state: 'error', note: e.message });
      }
    }
    notify?.(`${saved} of ${files.length} filed`, saved === files.length ? 'success' : 'info');
  };
  const busy = queue.some((x) => ['waiting', 'reading', 'saving'].includes(x.state));

  const toggleRemoved = async (ret, deleted) => {
    const r = await call('setDeleted', { unlock, id: ret.id, deleted });
    if (r.error === 'VAULT_LOCKED') { lock(); return; }
    if (!r.success) { notify?.(r.error || 'Failed', 'error'); return; }
    upsert(r.ret);
    notify?.(deleted ? 'Return removed — Restore is under “Show removed”' : 'Return restored', 'success');
  };

  // Newest year first, then entity.
  const shown = useMemo(() => returns
    .filter((r) => showRemoved || !r.deletedAt)
    .sort((a, b) => String(b.year).localeCompare(String(a.year)) || a.entity.localeCompare(b.entity)), [returns, showRemoved]);
  const removedCount = returns.filter((r) => r.deletedAt).length;
  const entities = Array.from(new Set([...knownEntities, ...returns.map((r) => r.entity)].filter(Boolean)));
  const today = todayIso();
  const payOf = (r) => (money ? returnPayment(r, money, today) : null);

  if (!status) return <div style={{ color: 'var(--hp-dim)' }}><Loader size={14} className="spin" /> Checking the vault…</div>;
  if (!unlock) return <VaultLock hasPassword={status.hasPassword} onUnlocked={onUnlocked} notify={notify} />;

  return (
    <>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: GREEN, fontSize: '.82rem', fontWeight: 600 }}><ShieldCheck size={15} /> Unlocked · encrypted at rest</span>
        <div style={{ flex: 1 }} />
        {removedCount > 0 && (
          <label style={{ fontSize: '.8rem', color: 'var(--hp-dim)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={showRemoved} onChange={(e) => setShowRemoved(e.target.checked)} /> Show removed ({removedCount})
          </label>
        )}
        <button className="btn btn-ghost btn-sm" onClick={() => setChanging((c) => !c)}><KeyRound size={14} /> Password</button>
        <button className="btn btn-secondary btn-sm" onClick={() => { load(); loadMoney(); }} title="Refresh"><RefreshCw size={14} /></button>
        <button className="btn btn-secondary btn-sm" onClick={lock}><Lock size={14} /> Lock</button>
        <button className="btn btn-primary btn-sm" onClick={() => setDrawer({ ret: null })}><Plus size={14} /> Add return</button>
      </div>

      {changing && <ChangePassword notify={notify} onClose={() => setChanging(false)} onDone={(t) => { onUnlocked(t); setChanging(false); }} />}

      <div className="card" style={{ ...dropZone, padding: 16, marginBottom: 14 }} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); if (!busy) ingest(e.dataTransfer?.files); }}>
        <label style={{ cursor: busy ? 'wait' : 'pointer', display: 'block' }}>
          <div style={{ fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 8 }}><Upload size={16} /> Drop returns here, or the accountant’s .zip</div>
          <div style={{ color: 'var(--hp-dim)', fontSize: '.8rem', marginTop: 4 }}>Each PDF is filed as its own return (or added to the one already here for that entity and year) · big files are fine</div>
          <input type="file" multiple accept="application/pdf,image/*,.zip,application/zip" style={{ display: 'none' }} disabled={busy} onChange={(e) => { ingest(e.target.files); e.target.value = ''; }} />
        </label>
        {queue.length > 0 && (
          <div style={{ marginTop: 12, textAlign: 'left', display: 'grid', gap: 4 }}>
            {queue.map((x, i) => (
              <div key={i} style={{ fontSize: '.8rem', display: 'flex', gap: 8, alignItems: 'center' }}>
                {x.state === 'done' ? <CheckCircle2 size={13} color={GREEN} /> : x.state === 'error' ? <X size={13} color={RED} /> : <Loader size={13} className={x.state === 'waiting' ? '' : 'spin'} />}
                <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '55%' }} title={x.name}>{x.name}</span>
                <span style={{ color: x.state === 'error' ? RED : 'var(--hp-dim)' }}>{x.note || (x.state === 'waiting' ? 'waiting' : '')}</span>
              </div>
            ))}
            {!busy && <button className="btn btn-ghost btn-sm" style={{ justifySelf: 'start' }} onClick={() => setQueue([])}>Clear</button>}
          </div>
        )}
      </div>

      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        {loading ? <div style={{ padding: 20, color: 'var(--hp-dim)' }}><Loader size={14} className="spin" /> Loading…</div> : !shown.length ? (
          <div style={{ padding: 24, color: 'var(--hp-dim)' }}>No returns yet. Drop the lodged return PDFs (or the accountant’s .zip) above. Claude reads the entity, the year and the figures.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
            <thead><tr>
              <th style={th}>Year</th><th style={th}>Entity</th><th style={th}>Lodged</th>
              <th style={{ ...th, ...num }}>Taxable income</th><th style={{ ...th, ...num }}>Tax</th><th style={{ ...th, ...num }}>Payable / refund</th><th style={th}>Paid?</th>
              <th style={th}>Files</th><th style={th} />
            </tr></thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.id} style={{ opacity: r.deletedAt ? 0.5 : 1 }}>
                  <td style={{ ...td, fontWeight: 700, whiteSpace: 'nowrap' }}>{r.year}</td>
                  <td style={td}><div style={{ fontWeight: 600 }}>{r.entity}</div><div style={{ color: 'var(--hp-dim)', fontSize: '.74rem' }}>{[r.entityType, r.agent].filter(Boolean).join(' · ')}</div></td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.lodged ? fmtDate(r.lodged) : '—'}</td>
                  <td style={{ ...td, ...num }}>{fmtMoney(r.taxableIncome)}</td>
                  <td style={{ ...td, ...num }}>{fmtMoney(r.taxPayable)}</td>
                  <td style={{ ...td, ...num, color: Number(r.result) < 0 ? GREEN : undefined }}>{r.result === '' || r.result == null ? '—' : Number(r.result) < 0 ? `${fmtMoney(-r.result)} refund` : fmtMoney(r.result)}</td>
                  <td style={td}><PayStamp pay={payOf(r)} /></td>
                  <td style={td}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {(r.docs || []).map((d) => (
                        <button key={d.id} className="btn btn-ghost btn-sm" style={{ justifyContent: 'flex-start', padding: '2px 6px' }} onClick={() => openDoc(r, d)} title={d.name}>
                          {opening === d.id ? <Loader size={12} className="spin" /> : <FileText size={12} />} {d.kind || 'File'}
                        </button>
                      ))}
                      <label className="btn btn-ghost btn-sm" style={{ justifyContent: 'flex-start', padding: '2px 6px', cursor: 'pointer', color: 'var(--hp-dim)' }}>
                        <Paperclip size={12} /> Add file
                        <input type="file" accept="application/pdf,image/*" style={{ display: 'none' }} onChange={(e) => { addFile(r, e.target.files?.[0]); e.target.value = ''; }} />
                      </label>
                    </div>
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button className="btn btn-ghost btn-sm" title="Edit" onClick={() => setDrawer({ ret: r })}><Pencil size={13} /></button>
                    {r.deletedAt
                      ? <button className="btn btn-ghost btn-sm" title="Restore" onClick={() => toggleRemoved(r, false)}><Undo2 size={13} /></button>
                      : <button className="btn btn-ghost btn-sm" title="Remove (can be restored)" style={{ color: RED }} onClick={() => toggleRemoved(r, true)}><Trash2 size={13} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {drawer && (
        <ReturnDrawer ret={drawer.ret} knownEntities={entities} unlock={unlock} notify={notify} onLocked={lock}
          onClose={() => setDrawer(null)} onSaved={(ret) => { upsert(ret); setDrawer(null); }} />
      )}
    </>
  );
}
